import { doc, Firestore, runTransaction } from "firebase/firestore";
import type { DocumentData } from "firebase/firestore";
import { firestore as FirestoreTypes } from "@foresting-tomorrow/api_types_firestore";
import { z } from "zod";

/** The backend creates the organization and membership atomically, then initializes it. */
export async function setupOnboardingOrganization(
  initialize: (data: Record<string, unknown>) => Promise<{ data: unknown }>,
  userId: string,
  data: Record<string, unknown>,
): Promise<string> {
  // Pick shared metadata fields so client IDs, roles, subscriptions and timestamps
  // never enter the signup request.
  const input = FirestoreTypes.org.pick({
    name: true, businessRegNo: true, address: true, zipcode: true, city: true, internalAppType: true,
  }).extend({
    // Registration numbers are optional identifiers, not phone numbers.
    businessRegNo: z.preprocess((value) => typeof value === "string" ? value.trim() || undefined : value ?? undefined, z.string().optional()),
    name: z.string().trim().min(1),
  }).parse(data);
  const organizationData = Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined));
  const result = await initialize({ organization: organizationData });
  const organization = FirestoreTypes.org.pick({ id: true }).parse(result.data);
  if (typeof organization.id !== "string" || !organization.id || organization.id.includes("/") || organization.id === userId) {
    throw new Error("init/invalid-organization-id");
  }
  return organization.id;
}

export function hasOrganizationMembership(
  userId: string,
  orgId: string,
  user: DocumentData | undefined,
  organization: DocumentData | undefined,
  privateData: DocumentData | undefined,
): boolean {
  const role = privateData?.roles?.[userId]?.role;
  return user?.orgId === orgId && Array.isArray(organization?.users) && organization.users.includes(userId) &&
    typeof role === "string" && ["admin", "editor", "viewer", "commentator", "buyer"].includes(role);
}

/** Read and complete in one transaction so cached/stale profile data cannot pass. */
export async function completeOnboardingProfile(db: Firestore, userId: string, profile: Record<string, unknown>) {
  await runTransaction(db, async (transaction) => {
    const userRef = doc(db, `users/${userId}`);
    const user = await transaction.get(userRef);
    const orgId = user.data()?.orgId;
    if (!orgId) throw new Error("init/organization-membership-not-ready");
    const organization = await transaction.get(doc(db, `orgs/${orgId}`));
    const privateData = await transaction.get(doc(db, `orgs/${orgId}/private_data/private`));
    if (!hasOrganizationMembership(userId, orgId, user.data(), organization.data(), privateData.data())) {
      throw new Error("init/organization-membership-not-ready");
    }
    const initialization = privateData.data()?.onboardingInitialization;
    if (initialization && initialization.status !== "ready") {
      throw new Error("init/organization-initialization-not-ready");
    }
    if (!organization.data()?.subscription?.type) throw new Error("init/subscription-not-ready");
    const { orgId: ignoredOrgId, orgRole, roles, ...profileFields } = profile;
    transaction.update(userRef, { ...profileFields, onboarded: true });
  });
}
